import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { FaCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fdb } from "../../firebase";

const BettingModal = ({ match, onClose, onPlaceBet }) => {
  const navigate = useNavigate();
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const quickAmounts = [100, 500, 1000, 2000];
  const potentialWin =
    selectedTeam && amount ? (amount * selectedTeam.value).toFixed(2) : "0.00";

  const handleBet = async () => {
    if (!selectedTeam || !amount) return;
    setError("");
    setIsLoading(true);

    try {
      await onPlaceBet(selectedTeam.team, amount, potentialWin);
      toast.success(
        <div className="font-medium">
          <p>Bet Placed Successfully! 🎉</p>
          <p className="text-sm mt-1">Amount: ₹{amount}</p>
          <p className="text-sm">Potential Win: ₹{potentialWin}</p>
        </div>,
        {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        }
      );
      onClose();
      navigate("/bet-history");
    } catch (err) {
      setError("Failed to place bet. Please try again.");
      toast.error("Failed to place bet. Please try again.", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^\d*\.?\d{0,2}$/.test(value) && value <= 10000)) {
      setAmount(value);
      setError("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gray-800 p-8 rounded-xl shadow-2xl w-96 border border-gray-700">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">Place Bet</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Match Info */}
        <div className="bg-gray-700/50 rounded-lg p-3 mb-6">
          <div className="text-sm text-gray-300">IPL {match.name}</div>
          {/* <div className="text-white font-medium">{match.teams.t1.name} vs {match.teams.t2.name}</div> */}
        </div>

        {/* Team Selection */}
        <div className="mb-6">
          <label className="block text-gray-400 text-sm font-medium mb-2">
            Select Team
          </label>
          <div className="grid grid-cols-2 gap-2">
            {match.odds.map((odd, index) => (
              <button
                key={index}
                onClick={() => setSelectedTeam(odd)}
                className={`p-3 rounded-lg border transition-all ${
                  selectedTeam?.team === odd.team
                    ? "bg-blue-600 border-blue-500 text-white"
                    : "bg-gray-700/50 border-gray-600 text-gray-300 hover:bg-gray-700"
                }`}
              >
                <div className="text-sm">
                  {match.teams[index === 0 ? "t1" : "t2"].name}
                </div>
                <div className="text-lg font-bold">{odd.value}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Amount Input */}
        <div className="mb-6">
          <label className="block text-gray-400 text-sm font-medium mb-2">
            Bet Amount
          </label>
          <input
            type="number"
            className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={amount}
            onChange={handleAmountChange}
            placeholder="Enter amount"
            min="0"
            max="10000"
          />
          <div className="grid grid-cols-4 gap-2 mt-2">
            {quickAmounts.map((quickAmount) => (
              <button
                key={quickAmount}
                onClick={() => setAmount(quickAmount)}
                className="bg-gray-700/50 text-gray-300 py-1 px-2 rounded border border-gray-600 hover:bg-gray-700 text-sm"
              >
                ₹{quickAmount}
              </button>
            ))}
          </div>
        </div>

        {/* Potential Win */}
        <div className="bg-gray-700/30 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Potential Win:</span>
            <span className="text-green-400 font-bold">₹{potentialWin}</span>
          </div>
        </div>

        {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

        {/* Actions */}
        <div className="flex gap-4">
          <button
            className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleBet}
            disabled={!selectedTeam || !amount || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              "Place Bet"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const statusMap = {
  0: "live",
  1: "upcoming",
  2: "finished",
  3: "no Result",
  4: "delay",
  5: "abandoned",
  6: "stumps",
};

const MatchCard = ({ match }) => {
  const navigate = useNavigate();
  const [showBettingModal, setShowBettingModal] = useState(false);
  const [selectedOdd, setSelectedOdd] = useState(null);

  // Handle both old and new data formats

  const handlePlaceBet = (selectedTeam, amount, potentialWin) => {
    console.log("Bet placed:", { match, selectedTeam, amount, potentialWin });
    setShowBettingModal(false);
  };

  const getStatusColor = (status) => {
    if (status === 0) return "bg-red-500/20 text-red-400";
    if (status === 1) return "bg-yellow-500/20 text-yellow-400";
    return "bg-blue-500/20 text-blue-400";
  };

  const handleCardClick = (e) => {
    // Prevent navigation if clicking on betting odds buttons
    if (!e.target.closest("button")) {
      navigate(`/match/${match.id}`);
    }
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-4 hover:bg-gray-800/70 transition-all cursor-pointer"
      >
        {/* Header with Match Info */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 p-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
              {match.format === "T20" && (
                <span className="text-[6px] text-white font-bold">T20</span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-300">
                {match.name} ({match.teams.t1.shortName} vs{" "}
                {match.teams.t2.shortName})
              </span>
              <span className="text-[10px] text-gray-400">{match.venue}</span>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <span
              style={{
                textTransform: "capitalize",
              }}
              className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getStatusColor(
                match.status
              )}`}
            >
              {statusMap[match.status]}
            </span>
            {match?.live?.score?.toss?.win && (
              <span className="text-[10px] text-gray-400 mt-0.5">
                Toss: {match.live.score.toss.win} Won and chose{" "}
                {match.live.score.toss.elec}
              </span>
            )}
          </div>
        </div>

        {/* Teams & Live Score */}
        <div className="mb-3 border-b border-gray-700/50 pb-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <div className="text-xs font-medium text-white truncate">
                {match.teams.t1?.name}
              </div>
              <div className="text-base font-bold text-white">
                {match?.live?.score?.teams?.t1?.i1?.over
                  ? `${match?.live?.score?.teams?.t1?.i1?.run}-${match?.live?.score?.teams?.t1?.i1?.wic}(${match?.live?.score?.teams?.t1?.i1?.over})`
                  : "Yet to bat"}
              </div>
              {/* {match.team?.overs && (
                <div className="text-[10px] text-gray-400">
                  {match.team1.overs} overs
                </div>
              )} */}
            </div>
            <div className="col-span-1 flex items-center justify-center">
              <span className="text-[10px] text-gray-400">VS</span>
            </div>
            <div className="col-span-1 text-right">
              <div className="text-xs font-medium text-white truncate">
                {match.teams.t2?.name}
              </div>
              <div className="text-base font-bold text-white">
                {match?.live?.score?.teams?.t2?.i1?.over
                  ? `${match?.live?.score?.teams?.t2?.i1?.run}-${match?.live?.score?.teams?.t2?.i1?.wic}(${match?.live?.score?.teams?.t2?.i1?.over})`
                  : "Yet to bat"}
              </div>
              {/* {match.team2?.overs && (
                <div className="text-[10px] text-gray-400">
                  {match.team2.overs} overs
                </div>
              )} */}
            </div>
          </div>
        </div>

        {/* Match Stats - More Compact */}
        {match.currentInnings && (
          <div className="grid grid-cols-4 gap-1 mb-3 p-2 bg-gray-900/30 rounded">
            <StatItem label="RR" value={match.currentInnings.runRate} />
            <StatItem
              label="Req RR"
              value={match.currentInnings.requiredRunRate}
            />
            <StatItem label="Target" value={match.currentInnings.target} />
            <StatItem label="Needs" value={match.currentInnings.required} />
          </div>
        )}

        {/* Last Ball Commentary - Optional, show only if space permits */}
        {match.lastBall && (
          <div className="mb-3 text-xs">
            <div className="text-gray-400 text-[10px]">Last ball:</div>
            <div className="text-white truncate">{match.lastBall}</div>
          </div>
        )}

        {/* Betting Odds - More Compact */}
        <div className="grid grid-cols-2 gap-2">
          {match.odds.map((odd, index) => (
            <button
              key={index}
              onClick={() => {
                if (match.status === 0 || match.status === 1) {
                  setSelectedOdd(odd);
                  setShowBettingModal(true);
                }
              }}
              className="group relative overflow-hidden"
            >
              {(match.status === 0 || match.status === 1) && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative p-2 bg-gray-700/50 border border-gray-600 rounded group-hover:border-blue-500 transition-all">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-300 group-hover:text-white truncate">
                        {match.teams[index === 0 ? "t1" : "t2"].name}
                      </span>
                      {odd.trend && (
                        <span
                          className={`text-[10px] ${
                            odd.trend === "up"
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          {odd.trend === "up" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                    <div className="text-base font-bold text-white group-hover:text-blue-200">
                      {odd.value}
                    </div>
                  </div>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Betting Modal remains unchanged */}
      {showBettingModal && (
        <BettingModal
          match={match}
          preSelectedTeam={selectedOdd}
          onClose={() => {
            setShowBettingModal(false);
            setSelectedOdd(null);
          }}
          onPlaceBet={handlePlaceBet}
        />
      )}
    </>
  );
};

const StatItem = ({ label, value }) => (
  <div className="text-center">
    <div className="text-[10px] text-gray-400">{label}</div>
    <div className="text-xs font-medium text-white">{value || "-"}</div>
  </div>
);

const MatchesSection = ({ title, matches, icon }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 mb-4">
      {icon}
      <h2 className="text-xl font-bold text-white">{title}</h2>
      <span className="text-sm text-gray-400">({matches.length})</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {matches.map((match, index) => (
        <MatchCard key={index} match={match} />
      ))}
    </div>
  </div>
);

const DashboardPage = () => {
  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { id: "all", label: "All" },
    { id: "live", label: "Live" },
    { id: "upcoming", label: "Upcoming" },
    { id: "recent", label: "Recent" },
  ];

  const matches = [
    // Live Matches
    {
      id: "ipl-2024-mi-csk",
      tournament: "IPL 2024",
      format: "T20",
      venue: "Wankhede Stadium, Mumbai",
      status: "Live - Second Innings",
      toss: "CSK won toss, chose to bowl",
      team1: {
        name: "Mumbai Indians",
        score: "185/4",
        overs: "20.0",
      },
      team2: {
        name: "Chennai Super Kings",
        score: "85/3",
        overs: "12.4",
      },
      currentInnings: {
        runRate: "6.71",
        requiredRunRate: "9.24",
        target: "186",
        required: "101 off 44",
      },
      lastBall: "Bumrah to Dhoni, SIX! Massive hit over long-on!",
      odds: [
        { team: "Mumbai Indians", value: "1.85", trend: "down" },
        { team: "Chennai Super Kings", value: "1.95", trend: "up" },
      ],
    },
    {
      id: "all-2024-sl-ind",
      tournament: "T20 Asian Legends League",
      format: "T20",
      venue: "R. Premadasa Stadium, Colombo",
      status: "Live - Innings Break",
      toss: "Indian Royals won toss, chose to field",
      team1: {
        name: "Sri Lankan Lions",
        score: "148/7",
        overs: "20.0",
      },
      team2: {
        name: "Indian Royals",
        score: "Yet to bat",
      },
      currentInnings: {
        runRate: "7.40",
        target: "149",
      },
      odds: [
        { team: "Sri Lankan Lions", value: "1.70", trend: "down" },
        { team: "Indian Royals", value: "2.10", trend: "up" },
      ],
    },

    // Upcoming Matches
    {
      id: "ipl-2024-rcb-gt",
      tournament: "IPL 2024",
      format: "T20",
      venue: "M. Chinnaswamy Stadium, Bangalore",
      status: "Upcoming - 7:30 PM IST",
      toss: "TBD",
      team1: {
        name: "Royal Challengers Bangalore",
      },
      team2: {
        name: "Gujarat Titans",
      },
      odds: [
        { team: "RCB", value: "1.90", trend: "stable" },
        { team: "Gujarat Titans", value: "2.00", trend: "stable" },
      ],
    },
    {
      id: "wc-2024-aus-eng",
      tournament: "ICC T20 World Cup 2024",
      format: "T20",
      venue: "MCG, Melbourne",
      status: "Upcoming - Tomorrow 6:00 PM IST",
      toss: "TBD",
      team1: {
        name: "Australia",
      },
      team2: {
        name: "England",
      },
      odds: [
        { team: "Australia", value: "1.80", trend: "stable" },
        { team: "England", value: "2.20", trend: "stable" },
      ],
    },

    // Recent Matches
    {
      id: "psl-2024-lq-kk",
      tournament: "PSL 2024",
      format: "T20",
      venue: "Gaddafi Stadium, Lahore",
      status: "Completed",
      toss: "Lahore Qalandars won toss, chose to bat",
      team1: {
        name: "Lahore Qalandars",
        score: "162/6",
        overs: "20.0",
      },
      team2: {
        name: "Karachi Kings",
        score: "153/8",
        overs: "20.0",
      },
      result: "Lahore Qalandars won by 9 runs",
      odds: [
        { team: "Lahore Qalandars", value: "1.65", trend: "won" },
        { team: "Karachi Kings", value: "2.30", trend: "lost" },
      ],
    },
    {
      id: "bbl-2024-sy-ms",
      tournament: "BBL 2024",
      format: "T20",
      venue: "SCG, Sydney",
      status: "Completed",
      toss: "Melbourne Stars won toss, chose to bowl",
      team1: {
        name: "Sydney Sixers",
        score: "198/5",
        overs: "20.0",
      },
      team2: {
        name: "Melbourne Stars",
        score: "180/7",
        overs: "20.0",
      },
      result: "Sydney Sixers won by 18 runs",
      odds: [
        { team: "Sydney Sixers", value: "1.55", trend: "won" },
        { team: "Melbourne Stars", value: "2.45", trend: "lost" },
      ],
    },
  ];

  // Categorize matches
  // const liveMatches = matches.filter((m) => m.status.includes("Live"));
  // const upcomingMatches = matches.filter((m) => m.status.includes("Upcoming"));
  // const recentMatches = matches.filter((m) => m.status.includes("Completed"));
  const [allMatches, setAllMatches] = useState([]);

  const upcomingMatches = allMatches.filter((m) => m.status === 1);
  const liveMatches = allMatches.filter((m) => m.status === 0);
  const recentMatches = allMatches.filter(
    (m) => m.status !== 1 && m.status !== 0
  );

  const getVisibleMatches = () => {
    switch (activeTab) {
      case "live":
        return liveMatches;
      case "upcoming":
        return upcomingMatches;
      case "recent":
        return recentMatches;
      default:
        return matches;
    }
  };

  useEffect(() => {
    const threeDayBeforeNowInUnixTimeStamp =
      Math.floor(Date.now() / 1000) - 3 * 24 * 60 * 60;
    const q = query(
      collection(fdb, "matches"),
      where("time", ">=", threeDayBeforeNowInUnixTimeStamp)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // This callback is executed whenever the data changes
        const upcomingMatchData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("Fetched data: ", upcomingMatchData);
        setAllMatches(
          upcomingMatchData.map((match) => {
            return {
              id: "bbl-2024-sy-ms",
              tournament: "BBL 2024",
              format: "T20",
              toss: "Melbourne Stars won toss, chose to bowl",
              team1: {
                name: "Sydney Sixers",
                score: "198/5",
                overs: "20.0",
              },
              team2: {
                name: "Melbourne Stars",
                score: "180/7",
                overs: "20.0",
              },
              result: "Sydney Sixers won by 18 runs",
              odds: [
                { team: "Sydney Sixers", value: "1.55", trend: "won" },
                { team: "Melbourne Stars", value: "2.45", trend: "lost" },
              ],
              ...match,
            };
          })
        );
        // setUsers(usersData);
        // setLoading(false);
      },
      (error) => {
        console.error("Error fetching users: ", error);
        // setLoading(false);
      }
    );

    // 4. Return the unsubscribe function to clean up when the component unmounts
    return () => unsubscribe();
  }, []);

  // useEffect(() => {
  //   const q = query(collection(fdb, "matches"), where("status", "==", 0));

  //   const unsubscribes = [];

  //   getDocs(q).then((querySnapshot) => {
  //     querySnapshot.docs.forEach((doc) => {
  //       const notificationRef = collection(
  //         fdb,
  //         "matches",
  //         doc.id,
  //         "notifications"
  //       );
  //       const q = query(notificationRef, where("createdAt", ">", Date.now()));
  //       const unsubscribe = onSnapshot(q, (snapshot) => {
  //         snapshot.docChanges().forEach((change) => {
  //           if (change.type === "added") {
  //             console.log("New message: ", change.doc.data());
  //             toast.info(
  //               <div
  //                 dangerouslySetInnerHTML={{
  //                   __html: change.doc.data().commentary,
  //                 }}
  //               />,
  //               // ,change.doc.data().commentary.replace(/<[^>]*>/g, "")
  //               {
  //                 position: "bottom-right",
  //               }
  //             );
  //           }
  //         });
  //       });
  //       unsubscribes.push(unsubscribe);
  //     });
  //   });

  //   return () => {
  //     for (const f of unsubscribes) {
  //       f();
  //     }
  //   };
  // }, []);

  return (
    <div className="min-h-screen bg-gray-900 pb-8">
      <div className=" px-4">
        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6 mt-4 border-b border-gray-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-2 relative ${
                activeTab === tab.id
                  ? "text-white"
                  : "text-gray-400 hover:text-gray-300"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
          ))}
        </div>

        {activeTab === "all" ? (
          <>
            {/* Live Matches Section */}
            {liveMatches.length > 0 && (
              <MatchesSection
                title="Live Matches"
                matches={liveMatches}
                icon={
                  <FaCircle className="text-red-500 animate-pulse" size={8} />
                }
              />
            )}

            {/* Upcoming Matches Section */}
            {upcomingMatches.length > 0 && (
              <MatchesSection
                title="Upcoming Matches"
                matches={upcomingMatches}
                icon={<FaCircle className="text-yellow-500" size={8} />}
              />
            )}

            {/* Recent Matches Section */}
            {recentMatches.length > 0 && (
              <MatchesSection
                title="Recent Matches"
                matches={recentMatches}
                icon={<FaCircle className="text-gray-500" size={8} />}
              />
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getVisibleMatches().map((match, index) => (
              <MatchCard key={index} match={match} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
